<template>
  <div class="wg-brief">
    <!-- Report / Content prompts — v2 segmented pills -->
    <div class="wg-tabs">
      <button type="button" class="wg-tab" :class="{ active: tab === 'report' }" @click="tab = 'report'">Report</button>
      <button type="button" class="wg-tab" :class="{ active: tab === 'prompts' }" @click="tab = 'prompts'">Content prompts</button>
    </div>

    <template v-if="tab === 'report'">
      <!-- Current POP score (publish .score-big) -->
      <div v-if="score != null" class="side-card">
        <div class="side-card-head">
          <h4>Current POP score</h4>
        </div>
        <div class="score-big">
          <div class="score-big-num" :class="scoreTone">{{ score }}<span class="denom">/100</span></div>
          <div class="score-big-lbl">{{ scoreLabel }}</div>
        </div>
        <!-- Draft-step "Update optimization score" block (exact ReportSidebar UX) -->
        <div class="score-stale" :class="{ show: scoreStale }">
          <span class="ico">!</span><span>You've changed the content since this score was worked out.</span>
        </div>
        <button class="score-update" type="button" :disabled="!scoreStale || scoring" @click="$emit('update-score')">
          <span v-if="scoring" class="sb-spin"></span>{{ scoring ? 'Re-scoring…' : 'Update optimization score' }}
        </button>
        <div class="score-checked" :class="{ show: !scoreStale && score != null }">Last checked just now</div>
      </div>

      <!-- Word count (publish .wc-row / .wc-bar) -->
      <div v-if="wcTarget" class="side-card">
        <div class="side-card-head"><h4>Word count</h4></div>
        <div class="wc-row">
          <div><span class="wc-num">{{ fmt(wcCurrent) }}</span><span class="wc-total"> / {{ fmt(wcTarget) }}</span></div>
          <div class="wc-helper">{{ wcHelper }}</div>
        </div>
        <div class="wc-bar"><div :style="{ width: wcPct + '%' }"></div></div>
      </div>

      <!-- Optimization breakdown (publish .sb-row + mini bars) -->
      <div v-if="hasSections" class="side-card">
        <div class="side-card-head"><h4>Optimization breakdown</h4></div>
        <div v-for="s in sectionsWithState" :key="s.key" class="sb-row">
          <span class="ico" :class="s.state === 'optimized' ? 'good' : 'warn'">{{ s.state === 'optimized' ? '✓' : '!' }}</span>
          <span class="lbl">{{ s.label }}</span>
          <div class="sb-mini-bar"><div :class="s.barClass" :style="{ width: s.pct + '%' }"></div></div>
        </div>
      </div>

      <!-- Per-section term cards (publish .sb-term-card) -->
      <div v-for="s in sectionsWithState" :key="'tc' + s.key" class="sb-term-card" :class="{ collapsed: collapsed[s.key] }">
        <div class="sb-term-head" @click="$set(collapsed, s.key, !collapsed[s.key])">
          <span v-if="s.state === 'optimized'" class="chk">✓</span>
          <span v-else class="chk-warn"><svg viewBox="0 0 24 24" fill="none" :stroke="s.state === 'underoptimized2' ? '#e5484d' : '#f0a030'" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg></span>
          <h4>{{ s.label }}</h4>
          <button type="button" class="chev" aria-label="Toggle section">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><polyline points="6 9 12 15 18 9"/></svg>
          </button>
        </div>
        <div class="sb-term-body">
          <div class="sb-term-counts" v-if="s.total">
            <div class="col"><div class="big">{{ s.total.current }}</div><div class="sub">Current count</div></div>
            <div class="divider"></div>
            <div class="col"><div class="big">{{ s.total.min }}-{{ s.total.max }}</div><div class="sub">Important terms range</div></div>
          </div>
          <table class="sb-term-table">
            <thead><tr><th>IMPORTANT TERM</th><th>CURRENT</th><th>TARGET</th></tr></thead>
            <tbody>
              <tr v-for="(item, i) in s.rows" :key="i">
                <td>
                  <div class="term"><span>{{ item.term.phrase }}</span></div>
                  <span v-if="item.term.type === 'keyword'" class="must-use">MUST USE</span>
                </td>
                <td><span class="cur">{{ item.contentBrief.current }} <span class="dot" :class="dotClass(s.key, item)"></span></span></td>
                <td class="tgt">{{ targetText(item) }}</td>
              </tr>
            </tbody>
          </table>
          <div v-if="excludedFor(s.key).length" class="wg-excluded">
            <div class="wg-excluded-lbl">Excluded terms</div>
            <span v-for="(t, i) in excludedFor(s.key)" :key="i" class="wg-excluded-chip">{{ t }}</span>
          </div>
        </div>
      </div>
    </template>

    <!-- Content prompts — legacy embed, themed by the .wg-side-v2 overrides.
         No outer card: the embed's own .white-common-box sections render as
         individual side-cards via the overrides. -->
    <div v-else class="wg-prompts">
      <content-prompt-embed
        :contentBrief="contentBrief"
        :keyword="keyword"
        :tagTotalData="tagTotalData"
        :wordCountData="wordCountData"
        :excludedTerms="excludedTerms"
        :competitors="competitors"
        :gCompetitors="gCompetitors"
        :relatedSearches="relatedSearches"
        :relatedQuestions="relatedQuestions"
        :page="page"
        :secretKey="secretKey"
        :isFromExtension="true"
        :showAsMobileView="true"
        :hideActionButtons="true"
      />
    </div>
  </div>
</template>

<script>
// Native publish-sidebar rendering of the content brief for the read-only
// (white-glove) viewer — same markup/CSS patterns as the AI Writer v2 Publish
// sidebar (.side-card / .score-big / .wc-* / .sb-row / .sb-term-card), fed by
// the report's contentBrief data via the existing cb_mixin helpers.
import { mixin as cb_mixin } from "../side-pane/ContentBrief/cb_mixin";
import { mixin as ContentBriefMixin } from "@/components/content_brief_mixin";
import ContentPromptEmbed from "../side-pane/ContentBrief/ContentPromptEmbed.vue";

// Legacy stoplight hex -> publish tone class (green/orange/purple/red).
const DOT_CLASS = { "#00CD49": "g", "#FF9B2D": "o", "#B886FF": "p", "#FF1F2D": "r" };

const SECTIONS = [
  { key: "title", label: "Search Engine Title", totalKey: "titleTotal" },
  { key: "pageTitle", label: "Page Title (H1)", totalKey: "pageTitleTotal" },
  { key: "subHeadings", label: "Sub-headings", totalKey: "subHeadingsTotal" },
  { key: "p", label: "Main Content", totalKey: "pTotal" },
];

export default {
  name: "WhiteGloveBriefSidebar",
  mixins: [cb_mixin, ContentBriefMixin],
  components: { ContentPromptEmbed },
  props: {
    contentBrief: { type: Object, default: () => ({}) },
    wordCountData: { type: Array, default: () => [0, 0] },
    fakeScore: { default: null },
    keyword: { type: String, default: "" },
    pageRun: { type: Object, default: () => ({}) },
    page: { type: Object, default: () => ({}) },
    tagTotalData: { default: null },
    excludedTerms: { default: null },
    gCompetitors: { default: null },
    relatedSearches: { default: null },
    relatedQuestions: { default: null },
    secretKey: { type: String, default: "" },
    // Update-optimization-score card state (Draft-step UX).
    scoreStale: { type: Boolean, default: false },
    scoring: { type: Boolean, default: false },
  },
  data() {
    return { tab: "report", collapsed: {} };
  },
  computed: {
    competitors() {
      return (this.pageRun && this.pageRun.competitors) || [];
    },
    score() {
      const s = this.fakeScore || ((this.contentBrief || {}).pageScore || {}).pageScore;
      return s != null ? Math.round(Number(s)) : null;
    },
    scoreTone() {
      if (this.score == null) return "tone-amber";
      if (this.score > 80) return "tone-green";
      if (this.score >= 70) return "tone-orange";
      return "tone-red";
    },
    scoreLabel() {
      return this.score != null && this.score >= 80 ? "Good" : "Needs work";
    },
    wcCurrent() {
      return (this.wordCountData && this.wordCountData[0]) || 0;
    },
    wcTarget() {
      return (this.wordCountData && this.wordCountData[1]) || 0;
    },
    wcPct() {
      return this.wcTarget ? Math.min(100, (this.wcCurrent / this.wcTarget) * 100) : 0;
    },
    wcHelper() {
      try {
        return this.calcWordCountRecommendation(this.wcCurrent, this.wcTarget);
      } catch (e) {
        return "";
      }
    },
    hasSections() {
      return this.sectionsWithState.length > 0;
    },
    // Sections that have data, each with rows / totals / stoplight state.
    sectionsWithState() {
      const cb = this.contentBrief || {};
      return SECTIONS.filter((s) => (cb[s.key] || []).length).map((s) => {
        const total = cb[s.totalKey] || null;
        let state = "optimized";
        try {
          if (total) state = this.getStopLightStateText(s.key, total.current, total.min, total.max, true);
        } catch (e) { /* keep default */ }
        return {
          ...s,
          rows: cb[s.key] || [],
          total,
          state,
          pct: total && total.min ? Math.min(100, (total.current / total.min) * 100) : 100,
          barClass: state === "optimized" ? "" : state === "underoptimized1" ? "tone-amber" : "tone-red",
        };
      });
    },
  },
  methods: {
    fmt(n) {
      return (n || 0).toLocaleString("en-US");
    },
    dotClass(sectionKey, item) {
      try {
        const hex = (this.getStopLightColorForItem(sectionKey, item) || "").toUpperCase();
        return DOT_CLASS[hex] || "o";
      } catch (e) {
        return "o";
      }
    },
    targetText(item) {
      const b = item.contentBrief || {};
      return item.term && item.term.type === "keyword" ? String(b.target != null ? b.target : "") : `${b.targetMin}-${b.targetMax}`;
    },
    excludedFor(key) {
      const ex = this.excludedTerms;
      if (!ex) return [];
      const list = ex[key];
      return Array.isArray(list) ? list : [];
    },
  },
};
</script>

<style scoped>
/* 1:1 with the Publish sidebar (PublishStep/ReportSidebar side-card set). */
.wg-brief { display: flex; flex-direction: column; gap: 12px; font-family: "Mulish", system-ui, sans-serif; }

/* Tab pills — exact .sb-view-toggle recipe from the publish sidebar (quiet
   segmented control: surface-2 track, white active pill, text-2 label). */
.wg-tabs { display: flex; width: 100%; background: var(--surface-2, #f7f7f7); border: 1px solid var(--border, #e4e4e4); border-radius: 7px; padding: 2px; }
.wg-tab { flex: 1; font-family: inherit; font-size: 11px; font-weight: 700; letter-spacing: 0.04em; text-transform: uppercase; color: var(--text-3, #6b7b7e); background: transparent; border: none; border-radius: 5px; padding: 5px 12px; cursor: pointer; transition: all 0.12s; text-align: center; }
.wg-tab:hover { color: var(--text-2, #014751); }
.wg-tab.active { background: #fff; color: var(--text-2, #014751); box-shadow: 0 1px 2px rgba(0, 0, 0, 0.08); }

/* Cards */
.side-card { background: #fff; border: 1px solid var(--border, #e4e4e4); border-radius: var(--r-lg, 10px); padding: 16px 18px; }
.side-card-head { display: flex; align-items: center; gap: 8px; margin-bottom: 10px; position: relative; }
.side-card-head h4 { flex: 1; margin: 0; font-size: 14px; font-weight: 700; color: var(--text, #052e33); display: flex; align-items: center; gap: 6px; }
.side-card-head .rc-icon { display: inline-flex; color: var(--text-3, #6b7b7e); }

/* Score (publish .score-big) */
.score-big { text-align: center; padding: 6px 0 2px; }
.score-big-num { font-size: 44px; font-weight: 700; line-height: 1; color: var(--text, #052e33); }
.score-big-num .denom { font-size: 18px; font-weight: 600; color: var(--text-3, #6b7b7e); }
.score-big-num.tone-green { color: var(--pop-green, #25a97a); }
.score-big-num.tone-orange { color: var(--pop-orange, #ff5700); }
.score-big-num.tone-amber { color: #aa993b; }
.score-big-num.tone-red { color: #e5484d; }
.score-big-lbl { font-size: 11px; color: var(--text-3, #6b7b7e); margin-top: 4px; }
/* Update-optimization-score block (exact ReportSidebar rules) */
.score-stale { display: none; gap: 8px; align-items: flex-start; margin-top: 12px; padding: 9px 11px; border-radius: 10px; background: var(--surface-2, #f7f7f7); border: 1px solid var(--border-strong, #ccc); font-size: 12px; line-height: 1.45; color: var(--text-2, #014751); }
.score-stale.show { display: flex; }
.score-stale .ico { flex-shrink: 0; width: 15px; height: 15px; border-radius: 50%; background: var(--pop-ink-2, #013840); color: #fff; font-size: 10px; font-weight: 800; display: inline-flex; align-items: center; justify-content: center; }
.score-update { width: 100%; justify-content: center; margin-top: 12px; display: inline-flex; align-items: center; gap: 7px; padding: 9px 12px; font-size: 13px; font-weight: 600; font-family: inherit; border-radius: 8px; background: #fff; border: 1.5px solid var(--pop-orange, #ff5700); color: var(--pop-orange-deep, #b23d00); cursor: pointer; transition: all 0.15s; }
.score-update:hover:not(:disabled) { background: var(--pop-orange-soft, #ffeddd); }
.score-update:disabled { opacity: 0.5; cursor: default; }
.score-checked { display: none; margin-top: 8px; font-size: 11px; color: var(--text-3, #6b7b7e); text-align: center; }
.score-checked.show { display: block; }
.sb-spin { width: 12px; height: 12px; border: 2px solid var(--pop-orange-tint, #ffd9c2); border-top-color: var(--pop-orange, #ff5700); border-radius: 50%; display: inline-block; animation: wg-sb-spin 0.7s linear infinite; }
@keyframes wg-sb-spin { to { transform: rotate(360deg); } }

/* Word count (publish .wc-*) */
.wc-row { display: flex; align-items: baseline; justify-content: space-between; gap: 10px; }
.wc-num { font-size: 22px; font-weight: 700; color: var(--text, #052e33); }
.wc-total { font-size: 13px; color: var(--text-3, #6b7b7e); font-weight: 600; }
.wc-helper { font-size: 11.5px; color: var(--text-3, #6b7b7e); text-align: right; }
.wc-bar { height: 4px; border-radius: 999px; background: #fce4d7; overflow: hidden; margin-top: 8px; }
.wc-bar > div { height: 100%; border-radius: 999px; background: linear-gradient(90deg, var(--pop-orange, #ff5700), #ff9966); }

/* Breakdown rows (publish .sb-row / .sb-mini-bar) */
.sb-row { display: flex; align-items: center; gap: 10px; padding: 5px 0; font-size: 12.5px; color: var(--text-2, #014751); }
.sb-row .ico { flex-shrink: 0; font-weight: 700; }
.sb-row .ico.good { color: var(--pop-green, #25a97a); }
.sb-row .ico.warn { color: #aa993b; }
.sb-row .lbl { flex: 1; font-weight: 500; }
.sb-mini-bar { width: 74px; height: 4px; border-radius: 999px; background: var(--surface-2, #f7f7f7); overflow: hidden; flex-shrink: 0; }
.sb-mini-bar > div { height: 100%; border-radius: 999px; background: var(--pop-green, #25a97a); }
.sb-mini-bar > div.tone-amber { background: #f0a030; }
.sb-mini-bar > div.tone-red { background: #e5484d; }

/* Term cards (publish .sb-term-*) */
.sb-term-card { background: #fff; border: 1px solid var(--border, #e4e4e4); border-radius: var(--r-lg, 10px); padding: 14px; }
.sb-term-head { display: flex; align-items: center; gap: 8px; cursor: pointer; user-select: none; }
.sb-term-head h4 { font-family: inherit; font-size: 14px; font-weight: 800; color: var(--text, #052e33); margin: 0; line-height: 1; }
.sb-term-head .chk { width: 14px; height: 14px; border-radius: 50%; background: var(--pop-green, #25a97a); color: #fff; font-size: 9px; font-weight: 700; display: inline-flex; align-items: center; justify-content: center; flex-shrink: 0; }
.sb-term-head .chk-warn { display: inline-flex; align-items: center; justify-content: center; flex-shrink: 0; line-height: 0; }
.sb-term-head .chk-warn svg { width: 16px; height: 16px; }
.sb-term-head .chev { margin-left: auto; flex-shrink: 0; width: 24px; height: 24px; border: none; background: transparent; cursor: pointer; color: var(--text-3, #6b7b7e); display: inline-flex; align-items: center; justify-content: center; border-radius: 6px; transition: background 0.12s, transform 0.2s; }
.sb-term-head .chev svg { width: 16px; height: 16px; display: block; }
.sb-term-head:hover .chev { background: var(--surface-2, #f7f7f7); color: var(--text-2, #014751); }
.sb-term-card.collapsed .chev { transform: rotate(-90deg); }
.sb-term-card.collapsed .sb-term-body { display: none; }
.sb-term-counts { display: flex; gap: 14px; padding: 10px 0 12px; }
.sb-term-counts .col { flex: 1; }
.sb-term-counts .big { font-size: 16px; font-weight: 800; color: var(--text, #052e33); }
.sb-term-counts .sub { font-size: 11px; color: var(--text-3, #6b7b7e); margin-top: 1px; }
.sb-term-counts .divider { width: 1px; background: var(--border, #e4e4e4); }
table.sb-term-table { width: 100%; border-collapse: collapse; border: 1px solid var(--border, #e4e4e4); border-radius: 6px; overflow: hidden; }
.sb-term-table th { text-align: left; font-size: 10.5px; font-weight: 700; color: var(--text-2, #014751); background: var(--surface-2, #f7f7f7); padding: 8px 10px; border-bottom: 1px solid var(--border, #e4e4e4); }
.sb-term-table td { padding: 9px 10px; border-bottom: 1px solid var(--border, #e4e4e4); font-size: 12px; color: var(--text-2, #014751); vertical-align: top; }
.sb-term-table tr:last-child td { border-bottom: none; }
.sb-term-table .term { display: flex; align-items: flex-start; gap: 7px; }
.sb-term-table .cur { display: inline-flex; align-items: center; gap: 6px; font-weight: 700; }
.sb-term-table .dot { width: 9px; height: 9px; border-radius: 50%; display: inline-block; }
.sb-term-table .dot.g { background: var(--pop-green, #25a97a); }
.sb-term-table .dot.o { background: #f0a030; }
.sb-term-table .dot.p { background: #9b6bd8; }
.sb-term-table .dot.r { background: #e5484d; }
.sb-term-table .tgt { font-variant-numeric: tabular-nums; }
.must-use { white-space: nowrap; display: inline-block; margin-top: 4px; background: #ff3939; color: #fff; font-size: 9.5px; font-weight: 700; padding: 2px 7px; border-radius: 4px; letter-spacing: 0.02em; }

/* Excluded terms */
.wg-excluded { margin-top: 10px; }
.wg-excluded-lbl { font-size: 10.5px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.04em; color: var(--text-3, #6b7b7e); margin-bottom: 6px; }
.wg-excluded-chip { display: inline-block; background: var(--surface-2, #f7f7f7); color: var(--text-2, #014751); border-radius: 100px; font-size: 12px; padding: 3px 10px; margin: 0 6px 6px 0; }

/* Content prompts wrapper (sections style themselves as cards) */
.wg-prompts { display: flex; flex-direction: column; gap: 0; }
</style>
